import { Game } from './core/Game.js';
import { globalEventBus, GameEvents } from './core/EventBus.js';
import { ConfettiSystem } from './ui/ConfettiSystem.js';
import { VictoryModal } from './ui/VictoryModal.js';
import { apiService } from './core/ApiService.js';
import { LeaderboardModal } from './ui/LeaderboardModal.js';
import { i18n } from './utils/I18n.js';

/**
 * 游戏页面入口
 */
class GamePage {
    constructor() {
        this.game = null;
        this.controlPanel = null;

        const params = new URLSearchParams(window.location.search);
        this.imageId = params.get('id');
        this.isCustomImage = params.get('custom') === 'true';

        // Systems
        this.confetti = new ConfettiSystem();
        this.leaderboardModal = new LeaderboardModal();
        this.victoryModal = new VictoryModal(
            () => this.restartGame(),
            () => window.location.href = 'index.html',
            () => {
                if (this.imageId && !this.isCustomImage) {
                    this.leaderboardModal.show(this.imageId);
                }
            }
        );

        // Debug: Expose victory modal to console
        window.debugVictoryModal = this.victoryModal;

        // 检查是否有有效的图片来源
        if (!this.isCustomImage && !this.imageId) {
            alert('No image specified! Redirecting to home...');
            window.location.href = 'index.html';
            return;
        }

        // 如果是自定义图片，检查 sessionStorage 中是否存在
        if (this.isCustomImage) {
            const customImageData = sessionStorage.getItem('customPuzzleImage');
            if (!customImageData) {
                alert('Custom image not found! Redirecting to home...');
                window.location.href = 'index.html';
                return;
            }
        }

        this.init();
    }

    restartGame() {
        this.game.startGame(this.getConfigFromUrl());
    }

    /**
     * 初始化应用
     */
    async init() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setup());
        } else {
            this.setup();
        }
    }

    /**
     * 设置游戏
     */
    async setup() {
        // 初始化 i18n（加载语言包）
        await i18n.init();

        // 创建控制面板 (简化版)
        this.createControlPanel();

        // 应用翻译到控制面板和页面静态文本
        i18n.applyTranslations();

        const gameContainer = document.getElementById('gameContainer');
        if (!gameContainer) {
            console.error('Game container not found');
            return;
        }

        // 初始化游戏
        this.game = new Game(gameContainer, {
            puzzle: this.getConfigFromUrl()
        });

        await this.game.ready;
        this.hideLoading();
        this.bindControls();
        this.bindGameEvents();

        // 根据来源加载图片
        if (this.isCustomImage) {
            await this.loadCustomImage();
        } else {
            await this.loadImageById(this.imageId);
        }
    }

    hideLoading() {
        const loading = document.querySelector('.loading');
        if (loading) loading.style.display = 'none';
    }

    createControlPanel() {
        this.controlPanel = document.getElementById('controls');
        if (!this.controlPanel) return;

        const title = this.isCustomImage ? i18n.t('game.custom_puzzle') : (this.imageId ? this.imageId.toUpperCase() : 'Puzzle');

        // 游戏页工具栏：左右分布，温馨舒适风格
        this.controlPanel.innerHTML = `
            <div class="toolbar-left">
                <button id="btnBack" class="btn btn-back">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
                    </svg>
                    <span data-i18n="game.back">Back</span>
                </button>
                <div class="toolbar-divider"></div>
                <div class="puzzle-title">
                    <span class="title-icon">
                        <svg viewBox="0 0 24 24">
                            <path d="M20.5 11H19V7c0-1.1-.9-2-2-2h-4V3.5C13 2.12 11.88 1 10.5 1S8 2.12 8 3.5V5H4c-1.1 0-1.99.9-1.99 2v3.8H3.5c1.49 0 2.7 1.21 2.7 2.7s-1.21 2.7-2.7 2.7H2V20c0 1.1.9 2 2 2h3.8v-1.5c0-1.49 1.21-2.7 2.7-2.7s2.7 1.21 2.7 2.7V22H17c1.1 0 2-.9 2-2v-4h1.5c1.38 0 2.5-1.12 2.5-2.5S21.88 11 20.5 11z"/>
                        </svg>
                    </span>
                    <span>${title}</span>
                </div>
            </div>
            <div class="toolbar-right">
                <button id="btnAchievements" class="btn btn-achievement">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94.63 1.5 1.98 2.63 3.61 2.96V19H7v2h10v-2h-4v-3.1c1.63-.33 2.98-1.46 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2zM5 8V7h2v3.82C5.84 10.4 5 9.3 5 8zm14 0c0 1.3-.84 2.4-2 2.82V7h2v1z"/>
                    </svg>
                    <span data-i18n="game.achievements">Achievements</span>
                </button>
                <button id="btnPreview" class="btn btn-preview">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                    </svg>
                    <span data-i18n="game.preview">Preview</span>
                </button>

                <div class="theme-select-wrapper">
                    <svg class="theme-select-icon" viewBox="0 0 24 24">
                        <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8zm-5.5 9c-.83 0-1.5-.67-1.5-1.5S5.67 9 6.5 9 8 9.67 8 10.5 7.33 12 6.5 12zm3-4C8.67 8 8 7.33 8 6.5S8.67 5 9.5 5s1.5.67 1.5 1.5S10.33 8 9.5 8zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 5 14.5 5s1.5.67 1.5 1.5S15.33 8 14.5 8zm3 4c-.83 0-1.5-.67-1.5-1.5S16.67 9 17.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                    </svg>
                    <select id="selectTheme" class="theme-select">
                        <option value="0">Default</option>
                        <option value="1">Dark Blue</option>
                        <option value="2">Fresh Blue</option>
                        <option value="3">Vintage Wood</option>
                        <option value="4">Minimalist</option>
                        <option value="5">color5</option>
                    </select>
                    <svg class="theme-select-arrow" viewBox="0 0 24 24">
                        <path d="M7 10l5 5 5-5z"/>
                    </svg>
                </div>

                <button id="btnRestart" class="btn btn-primary">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                    </svg>
                    <span data-i18n="game.restart">Restart</span>
                </button>
            </div>
        `;
    }

    getConfigFromUrl() {
        const params = new URLSearchParams(window.location.search);
        return {
            cols: parseInt(params.get('cols') || 5),
            rows: parseInt(params.get('rows') || 4),
            pieceSize: parseInt(params.get('pieceSize') || 80),
            tabSize: parseInt(params.get('tabSize') || 20) / 200,
            seed: parseInt(params.get('seed') || 1234)
        };
    }

    bindControls() {
        document.getElementById('btnBack')?.addEventListener('click', () => {
            window.location.href = 'index.html';
        });

        document.getElementById('btnAchievements')?.addEventListener('click', () => {
            if (this.game.achievementModal) {
                this.game.achievementModal.show();
            }
        });

        document.getElementById('btnPreview')?.addEventListener('click', () => {
            this.showPreview();
        });

        document.getElementById('selectTheme')?.addEventListener('change', (e) => {
            if (this.game.puzzleBoard) {
                const index = parseInt(e.target.value);
                this.game.puzzleBoard.setTheme(index);
                // 移除焦点，防止后续键盘操作干扰（如空格键重新触发选择）
                e.target.blur();
            }
        });

        document.getElementById('btnRestart')?.addEventListener('click', () => {
            this.game.startGame(this.getConfigFromUrl());
        });
        
        // Disable default drop behavior to prevent opening image in browser
        window.addEventListener('dragover', e => e.preventDefault());
        window.addEventListener('drop', e => e.preventDefault());
    }

    showPreview() {
        if (this.game.puzzleBoard) {
            this.game.puzzleBoard.showPreviewAnimated(2000, 500);
        }
    }

    bindGameEvents() {
        globalEventBus.on(GameEvents.PIECE_SNAP, () => {
            const progress = this.game.puzzleBoard.getProgress();
            this.game.uiManager.updateProgress(progress);
        });

        globalEventBus.on(GameEvents.GAME_COMPLETE, () => {
            console.log('Puzzle completed! (UI)');

            // Show Modal after a short delay
            setTimeout(() => {
                const state = this.game.getState();
                const timeStr = this.game.timer.getFormattedTime();
                this.victoryModal.show(timeStr, state.score);

                // Play Confetti AFTER the modal appears
                // Wait 300ms for the modal animation to start
                setTimeout(() => {
                    this.confetti.playVictoryConfetti();
                }, 300);
            }, 1000);
        });
    }

    async loadImageById(id) {
        try {
            // Use glob import to locate the file reliably in all asset folders
            const images = import.meta.glob([
                './assets/picture/*.{jpg,JPG}',
                './assets/c1/*.{jpg,JPG,png,PNG}',
                './assets/c2/*.{jpg,JPG,png,PNG}',
                './assets/banner/*.{jpg,JPG,png,PNG}'
            ], { eager: true });

            // Find key where filename matches id
            const matchingKey = Object.keys(images).find(key => {
                // Extract filename without extension
                const fileName = key.split('/').pop().replace(/\.[^/.]+$/, "");
                return fileName.toLowerCase() === id.toLowerCase();
            });

            if (!matchingKey) {
                throw new Error(`Image not found: ${id}`);
            }

            const module = images[matchingKey];
            const imageUrl = module.default || module;
            this.currentImageUrl = imageUrl; // Save for preview

            const texture = await this.game.imageLoader.load(imageUrl);
            this.game.state.currentImage = texture;
            await this.game.startGame(this.getConfigFromUrl());
        } catch (error) {
            console.error(`Failed to load image ${id}:`, error);
            alert('Failed to load image. Returning home.');
            window.location.href = 'index.html';
        }
    }

    /**
     * 加载自定义上传的图片
     */
    async loadCustomImage() {
        try {
            const customImageData = sessionStorage.getItem('customPuzzleImage');

            if (!customImageData) {
                throw new Error('Custom image not found in session storage');
            }

            this.currentImageUrl = customImageData; // Save for preview

            const texture = await this.game.imageLoader.load(customImageData);
            this.game.state.currentImage = texture;
            await this.game.startGame(this.getConfigFromUrl());
        } catch (error) {
            console.error('Failed to load custom image:', error);
            alert('Failed to load custom image. Returning home.');
            window.location.href = 'index.html';
        }
    }
}

new GamePage();
